import { useId, useState, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface DisclosureProps {
  title: ReactNode
  /** One line beside the title, shown folded and open. */
  hint?: ReactNode
  defaultOpen?: boolean
  children: ReactNode
  className?: string
}

/** A section folded under its title ("iTunes에서 찾기", "키워드 제안"): the
 *  secondary tool at the foot of a page that the page does not need open. */
export function Disclosure({ title, hint, defaultOpen = false, children, className }: DisclosureProps) {
  const [open, setOpen] = useState(defaultOpen)
  const id = useId()
  return (
    <section className={cn('rounded-lg border border-border bg-card', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <ChevronRight size={16} aria-hidden className={cn('shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
        {/* the hint goes under the title on a phone, so the title never folds */}
        <span className="flex min-w-0 flex-1 flex-col md:flex-row md:items-baseline md:gap-2">
          <span className="whitespace-nowrap text-section font-semibold">{title}</span>
          {hint && <span className="min-w-0 truncate text-meta text-muted-foreground">{hint}</span>}
        </span>
      </button>
      {open && (
        <div id={id} className="space-y-3 border-t border-border px-4 py-4">
          {children}
        </div>
      )}
    </section>
  )
}
